// ── Enterprise page content ───────────────────────────────────────────────────

export const ENTERPRISE_HERO = {
  eyebrow: "ENTERPRISE",
  title: "Production-grade from day one.",
  intro:
    "Run the digital replica of your product on your own infrastructure. Single sign-on, isolation, auditability, and observability are part of the platform, not an afterthought — deploy on-premises, in your cloud, or air-gapped with no changes to the architecture.",
};

export const CAPABILITY_CARDS: { title: string; body: string }[] = [
  {
    title: "SSO via OIDC",
    body: "Bearer-JWT authentication with JWKS discovery. RS256 and ES256 only — `alg:none` is rejected at the gateway. Works out of the box with Okta, Auth0, Keycloak, and Zitadel.",
  },
  {
    title: "Multi-tenant by design",
    body: "Per-tenant isolation with fine-grained quotas on projects, models, notebooks, runs, and concurrent sessions. Tenants are created lazily on first login; per-session metering feeds your billing pipeline.",
  },
  {
    title: "Session isolation",
    body: "Each compute session runs in a sandboxed worker: read-only rootfs, no network by default, seccomp filter applied. Firecracker microVM and gVisor runtimes are supported deployment targets.",
  },
  {
    title: "Audit logging",
    body: "Append-only audit trail with content-hash and version on every mutation. Logs are streamed to object storage with object-lock immutability — tamper evidence without custom middleware.",
  },
  {
    title: "Observability (OTLP)",
    body: "Distributed OpenTelemetry traces across the gateway, workers, and frontend SPA. SLO targets are defined for cell round-trip, simulation start, and scope latency. Drop into Grafana, Datadog, or Jaeger.",
  },
  {
    title: "Rate limiting & quotas",
    body: "Token-bucket rate limits per asset type with structured 429 responses on breach. Limit metadata (remaining, reset-at) is included in the response body for clean client-side handling.",
  },
  {
    title: "SCIM provisioning",
    body: "SCIM user and group provisioning path for RBAC per project. Sync identities and roles directly from your IdP — no manual account creation, no stale access.",
  },
  {
    title: "Data residency & disaster recovery",
    body: "SurrealDB metadata and S3/MinIO blobs stay in the region you choose. Hourly snapshots, plus GIR-hash run provenance enables bit-exact replay of any past simulation from scratch.",
  },
];

export const OPEN_PORTABLE = {
  eyebrow: "OPEN & PORTABLE",
  title: "No lock-in. Your models are yours.",
  intro:
    "DjiniousLab is built on open standards end to end. Models import and export as FMI 3.0 co-simulation units — compatible with OpenModelica, Dymola, and any FMI-compliant tool. Projects live in plain git repositories. The model registry publishes versioned artifacts you can depend on from CI. Codegen produces human-readable Rust firmware that compiles without the platform. For air-gapped or on-premises deployments the full stack — gateway, SurrealDB, object store, workers — runs in a single compose file with no external dependencies.",
};

export const TRUST_PILLS: { label: string; tone: "neutral" | "primary" | "accent" }[] = [
  { label: "STRIDE threat model", tone: "primary" },
  { label: "TLS enforced end-to-end", tone: "neutral" },
  { label: "SBOM per artifact", tone: "accent" },
  { label: "Signed run attestations", tone: "primary" },
  { label: "Pinned toolchains", tone: "neutral" },
  { label: "Trivy + cargo-audit scanning", tone: "accent" },
  { label: "Semgrep SAST", tone: "neutral" },
  { label: "SOC 2-aligned threat model", tone: "primary" },
];

export const ENTERPRISE_STATS: { value: string; label: string }[] = [
  { value: "OIDC", label: "SSO" },
  { value: "Multi-tenant", label: "isolation" },
  { value: "OTLP", label: "tracing" },
  { value: "FMI 3.0", label: "interop" },
];

export const ENTERPRISE_CTA = {
  heading: "Bring DjiniousLab to your team.",
  body: "We'll walk you through the deployment model, security posture, and integration points — tailored to your stack.",
  cta: { label: "Book a demo", to: "/book-demo" },
};
